import './Layout.scss';

import { Capacitor } from '@capacitor/core';
import { ConfigContext, isDefaultBrand } from '@travelwin/core';
import classNames from 'classnames';
import {
  ForwardedRef,
  forwardRef,
  ReactNode,
  RefObject,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';

import MobileNavFooter from '../MobileNavFooter';
import Navbar from '../Navbar';
import { ZendeskContext } from '../Zendesk';

const className = 'c-Layout';

type LayoutProps = {
  children: ReactNode;
  header?: ReactNode;
  centered?: boolean;
  showNavBar?: boolean;
  zendesk?: 'show' | 'hide' | 'keep';
};

export type ForwardedLayoutReferences = {
  readonly navbar: RefObject<HTMLElement>;
  readonly mobileNavFooter: RefObject<HTMLDivElement>;
};

const isNativePlatform = Capacitor.isNativePlatform();

const LayoutComponent = (
  {
    children,
    header,
    centered,
    showNavBar = true,
    zendesk = 'hide',
  }: LayoutProps,
  forwardedRef: ForwardedRef<ForwardedLayoutReferences>,
) => {
  // this component is used in error boundaries, not all context providers
  // might be available and the code should account for that
  const showZendesk = useContext(ZendeskContext);
  const config = useContext(ConfigContext);
  const navbarRef = useRef<HTMLElement>(null);
  const mobileNavFooterRef = useRef<HTMLDivElement>(null);

  // exposing references to particular layout HTML elements
  useImperativeHandle(forwardedRef, () => ({
    get navbar() {
      return navbarRef;
    },
    get mobileNavFooter() {
      return mobileNavFooterRef;
    },
  }));

  useEffect(() => {
    if (showZendesk && zendesk !== 'keep') {
      showZendesk(zendesk === 'show');
    }
  }, [showZendesk, zendesk]);

  return (
    <>
      {Capacitor.getPlatform() === 'ios' && (
        <div className={`${className}__shell--status-bar`} />
      )}
      <div
        className={classNames(
          `${className}__shell--${Capacitor.getPlatform()}`,
          {
            [`${className}__shell--native`]: isNativePlatform,
            [`--has-nav-bar`]: showNavBar,
            [`--default-brand`]:
              config && isDefaultBrand(config.WHITELABEL_BRAND),
          },
        )}
      >
        {showNavBar && !isNativePlatform && <Navbar ref={navbarRef} />}

        <div
          className={classNames(
            `${className}__frame`,
            `${className}__frame--${Capacitor.getPlatform()}`,
            {
              [`${className}__frame--has-header`]: Boolean(header),
            },
          )}
        >
          {header && (
            <div className={`${className}__frame__header`}>{header}</div>
          )}
          <div
            className={classNames(`${className}__frame--content`, {
              [`${className}__frame--content--centered`]: centered,
            })}
          >
            {children}
          </div>
        </div>

        {showNavBar && isNativePlatform && (
          <MobileNavFooter ref={mobileNavFooterRef} />
        )}
      </div>
    </>
  );
};

export const Layout = forwardRef<ForwardedLayoutReferences, LayoutProps>(
  LayoutComponent,
);
